import * as z from 'zod';
import { brandSchema, experienceSchema, marketSchema } from './schema';

const flagsSchema = z.object({
  selectionVin: z.boolean().default(false),
  selectionCode: z.boolean().default(false),
  selectionCarline: z.boolean().default(false),
  selectionDCC: z.boolean().default(false),
  selectionSCC: z.boolean().default(false),
  sharingDga: z.boolean().default(false),
  sharingSameDevice: z.boolean().default(false),
  sharingBroadcast: z.boolean().default(false), // Broadcast to ChromeBox — shows presentation on local TV via cloud 3D rendering
  sharingBroadcastLocal: z.boolean().default(false), // Broadcast to Local Renderer — uses local renderer to render and present 3D stream
  sharingRemote: z.boolean().default(false),
  voip: z.boolean().default(false),
  wltp: z.boolean().default(false),
  brandSwitcher: z.boolean().default(false),
  localeSwitcher: z.boolean().default(false),
  '2D': z.boolean().default(false),
  sessionTimeout: z.boolean().default(true), // enable/disable session timeout feature
  scheduler: z.boolean().default(false), // enable/disable scheduling feature
  comparison: z.boolean().default(false), // enable/disable vehicle comparison feature
  dgaCamFlight: z.boolean().default(true), // enable/disable DGA camera flight option
  carDetails: z.boolean().default(false), // enable/disable car details display
  download2dImage: z.boolean().default(false),
  download3dSnapshot: z.boolean().default(false),
  downloadComparisonImage: z.boolean().default(false),
});

const sessionTimeoutSchema = z
  .object({
    timeoutMinutes: z.number().min(1).max(240), // timeout in minutes, min 1 minute, max 4 hours
    warningMinutes: z.number().min(0), // warning time in minutes before timeout
  })
  .transform((data) => {
    // If timeout is 1 minute and warning is 0, set warning to 0.3
    if (data.timeoutMinutes === 1 && data.warningMinutes === 0) {
      return { ...data, warningMinutes: 0.3 };
    }
    return data;
  })
  .refine(
    (data) => {
      return data.warningMinutes < data.timeoutMinutes;
    },
    {
      message: 'Warning period must be less than timeout duration',
      path: ['warningMinutes'],
    }
  );

const schedulerConfigSchema = z.object({
  minAdvanceMinutes: z.number().min(60).default(60), // minimum time in advance to schedule (in minutes)
  startWindowBeforeMinutes: z.number().min(0).default(5), // how many minutes before scheduled time the session can be started
  startWindowAfterMinutes: z.number().min(0).default(30), // how many minutes after scheduled time the session can be started
});

const accessControlSchema = z.object({
  dealerWhitelistEnabled: z.boolean().default(false), // when true, only dealers in dealerWhitelist can access the app
  dealerWhitelist: z.array(z.string()).default([]), // list of dealer IDs allowed to access when whitelisting is enabled
});

const orderableSharingFlagSchema = z.enum([
  'sharingRemote',
  'sharingBroadcast',
  'sharingBroadcastLocal',
  'sharingSameDevice',
]);

export type OrderableSharingFlag = z.infer<typeof orderableSharingFlagSchema>;

const webConfigurator = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  name: z.string().default(''),
  language: z.string().default('English').optional(),
  isReleased: z.boolean().default(true),
});

const dccSchema = webConfigurator.extend({
  dccLink: z.string().default(''),
});

const sccSchema = webConfigurator.extend({
  sccLink: z.string().default(''),
});

export const tenantConfigSchema = z
  .object({
    id: z.string().optional(),
    market: marketSchema.default('*'),
    brand: brandSchema.default('*'),
    tenant: z.string().default('*'), // Might be used for multi-tenancy in the future
    authRequired: z.boolean().default(true),
    flags: flagsSchema.optional().default({
      selectionVin: false,
      selectionCode: false,
      selectionCarline: false,
      selectionDCC: false,
      selectionSCC: false,
      sharingDga: false,
      sharingSameDevice: false,
      sharingBroadcast: false,
      sharingBroadcastLocal: false,
      sharingRemote: false,
      voip: false,
      wltp: false,
      brandSwitcher: false,
      localeSwitcher: false,
      '2D': false,
      sessionTimeout: true,
      scheduler: false,
      comparison: false,
      dgaCamFlight: true,
      carDetails: false,
      download2dImage: false,
      download3dSnapshot: false,
      downloadComparisonImage: false,
    }),
    defaultExperience: experienceSchema.default('3D'),
    dcc: z.array(dccSchema).default([]),
    scc: z.array(sccSchema).default([]),
    sessionTimeout: sessionTimeoutSchema.default({
      timeoutMinutes: 20,
      warningMinutes: 3,
    }),
    scheduler: schedulerConfigSchema.default({
      minAdvanceMinutes: 60,
      startWindowBeforeMinutes: 5,
      startWindowAfterMinutes: 30,
    }),
    accessControl: accessControlSchema.default({
      dealerWhitelistEnabled: false,
      dealerWhitelist: [],
    }),
    // Order of sharing options in Sharing Dashboard. Empty array = canonical default order.
    sharingOptionsOrder: z
      .array(orderableSharingFlagSchema)
      .default([])
      .refine((arr) => arr.length === new Set(arr).size, {
        message: 'sharingOptionsOrder must not contain duplicate entries',
      }),
    enabled: z.boolean().default(true),
  })
  .transform((data) => ({
    ...data,
    id: data.id || `${data.brand}-${data.market}-${data.tenant}`,
  }));

export { flagsSchema };
export type ITenantConfig = z.infer<typeof tenantConfigSchema>;
export type IFeatureFlags = z.infer<typeof flagsSchema>;
export type IDccConfig = z.infer<typeof dccSchema>;
export type ISccConfig = z.infer<typeof sccSchema>;
export type ISessionTimeoutConfig = z.infer<typeof sessionTimeoutSchema>;
export type ISchedulerConfig = z.infer<typeof schedulerConfigSchema>;
export type IAccessControlConfig = z.infer<typeof accessControlSchema>;
